import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { FileText, Save, Eye, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { refreshPageContent } from "@/hooks/usePageContent";

export default function PageContentCard() {
  const [registry, setRegistry] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewKey, setPreviewKey] = useState(null);

  useEffect(() => {
    api.get("/page-content/registry").then((r) => {
      setRegistry(r.data.registry);
      // deep clone effective values as the editable draft
      setDraft(JSON.parse(JSON.stringify(r.data.values || {})));
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  if (!registry) return <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm text-sm text-slate-500" data-testid="page-content-card">Loading…</div>;

  const setField = (pageKey, sectionKey, fieldKey, value) =>
    setDraft((d) => ({ ...d, [pageKey]: { ...d[pageKey], [sectionKey]: { ...d[pageKey]?.[sectionKey], [fieldKey]: value } } }));
  const setEnabled = (pageKey, sectionKey, value) =>
    setDraft((d) => ({ ...d, [pageKey]: { ...d[pageKey], [sectionKey]: { ...d[pageKey]?.[sectionKey], enabled: value } } }));

  const sectionDefaults = (s) => {
    const o = {};
    for (const f of s.fields) o[f.key] = f.default;
    if (s.toggleable) o.enabled = s.enabled !== false;
    return o;
  };
  const resetSection = (page, s) => setDraft((d) => ({ ...d, [page.key]: { ...d[page.key], [s.key]: sectionDefaults(s) } }));
  const resetPage = (page) => setDraft((d) => {
    const p = {};
    for (const s of page.sections) p[s.key] = sectionDefaults(s);
    return { ...d, [page.key]: p };
  });
  const isSectionDirty = (page, s) => {
    const cur = draft[page.key]?.[s.key] || {};
    if (s.toggleable && (cur.enabled !== false) !== (s.enabled !== false)) return true;
    return s.fields.some((f) => (cur[f.key] ?? f.default) !== f.default);
  };
  const isPageDirty = (page) => page.sections.some((s) => isSectionDirty(page, s));

  const save = async () => {
    const overrides = {};
    for (const page of registry) {
      overrides[page.key] = {};
      for (const s of page.sections) {
        const cur = draft[page.key]?.[s.key] || {};
        const fields = {};
        for (const f of s.fields) fields[f.key] = cur[f.key] ?? f.default;
        const entry = { fields };
        if (s.toggleable) entry.enabled = cur.enabled !== false;
        overrides[page.key][s.key] = entry;
      }
    }
    setSaving(true);
    try {
      await api.put("/page-content", { overrides });
      await refreshPageContent();
      toast.success("Page content saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const previewPage = registry.find((p) => p.key === previewKey);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4" data-testid="page-content-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold text-[#0B1A2F] flex items-center gap-2"><FileText size={18} /> Page content</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">Edit the wording on each page and switch individual sections on or off. Turn a section off (e.g. the EAP card) if it doesn't apply yet — staff simply won't see it. Preview your changes, then save to make them live for everyone.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#0B1A2F] hover:bg-[#162B48]" data-testid="page-content-save-btn"><Save size={15} className="mr-1.5" /> {saving ? "Saving…" : "Save changes"}</Button>
      </div>
      <Accordion type="multiple" className="space-y-2">
        {registry.map((page) => (
          <AccordionItem key={page.key} value={page.key} className="border border-slate-200 rounded-lg px-4" data-testid={`page-content-page-${page.key}`}>
            <AccordionTrigger className="hover:no-underline py-3 text-[#0B1A2F] font-medium">{page.label}</AccordionTrigger>
            <AccordionContent className="pb-4 space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => setPreviewKey(page.key)} data-testid={`page-content-preview-${page.key}`}><Eye size={14} className="mr-1.5" /> Preview page</Button>
                <Button variant="ghost" size="sm" disabled={!isPageDirty(page)} onClick={() => resetPage(page)} className="text-slate-500 hover:text-[#0B1A2F]" data-testid={`page-content-reset-page-${page.key}`}><RotateCcw size={13} className="mr-1.5" /> Reset page</Button>
              </div>
              {page.sections.map((s) => {
                const cur = draft[page.key]?.[s.key] || {};
                return (
                  <div key={s.key} className="rounded-lg border border-slate-100 bg-slate-50/50 p-4" data-testid={`page-content-section-${page.key}-${s.key}`}>
                    <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                      <span className="text-sm font-semibold text-[#0B1A2F]">{s.label}</span>
                      <div className="flex items-center gap-3">
                        {isSectionDirty(page, s) && (
                          <button type="button" onClick={() => resetSection(page, s)} className="text-[11px] text-[#2EA3FD] hover:underline flex items-center gap-1" data-testid={`page-content-reset-section-${page.key}-${s.key}`}><RotateCcw size={11} /> Reset section</button>
                        )}
                        {s.toggleable && (
                          <label className="flex items-center gap-2 text-xs text-slate-500">
                            {cur.enabled !== false ? "Shown" : "Hidden"}
                            <Switch checked={cur.enabled !== false} onCheckedChange={(v) => setEnabled(page.key, s.key, v)} data-testid={`page-content-toggle-${page.key}-${s.key}`} />
                          </label>
                        )}
                      </div>
                    </div>
                    <div className="grid gap-3">
                      {s.fields.map((f) => {
                        const isDefault = (cur[f.key] ?? f.default) === f.default;
                        return (
                          <div key={f.key}>
                            <div className="flex items-center justify-between gap-2">
                              <Label className="text-xs text-slate-500">{f.label}</Label>
                              {!isDefault && (
                                <button type="button" onClick={() => setField(page.key, s.key, f.key, f.default)} className="text-[11px] text-[#2EA3FD] hover:underline flex items-center gap-1" data-testid={`page-content-reset-${page.key}-${s.key}-${f.key}`}><RotateCcw size={11} /> Reset to default</button>
                              )}
                            </div>
                            {f.type === "textarea" ? (
                              <Textarea rows={2} value={cur[f.key] ?? ""} onChange={(e) => setField(page.key, s.key, f.key, e.target.value)} className="mt-1" data-testid={`page-content-field-${page.key}-${s.key}-${f.key}`} />
                            ) : (
                              <Input type={f.type === "url" ? "url" : "text"} value={cur[f.key] ?? ""} onChange={(e) => setField(page.key, s.key, f.key, e.target.value)} className="mt-1" data-testid={`page-content-field-${page.key}-${s.key}-${f.key}`} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <Sheet open={!!previewKey} onOpenChange={(o) => { if (!o) setPreviewKey(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="page-content-preview-panel">
          <SheetHeader><SheetTitle className="flex items-center gap-2"><Eye size={16} className="text-[#2EA3FD]" /> Preview · {previewPage?.label}</SheetTitle><SheetDescription>A preview of your unsaved page content changes.</SheetDescription></SheetHeader>
          {previewPage && <PagePreview page={previewPage} values={draft[previewPage.key] || {}} />}
          <p className="text-xs text-slate-400 mt-5">Showing your unsaved changes. Close this panel and click <strong>Save changes</strong> to make them live for everyone.</p>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function PagePreview({ page, values }) {
  const hero = page.sections.find((s) => s.key === "hero");
  const rest = page.sections.filter((s) => s.key !== "hero");
  const val = (s, k) => {
    const stored = values[s.key]?.[k];
    if (stored !== undefined && stored !== "") return stored;
    return s.fields.find((f) => f.key === k)?.default;
  };
  return (
    <div className="mt-4 space-y-4" data-testid="page-preview-body">
      {hero && (
        <div className="rounded-xl bg-gradient-to-br from-[#0B1A2F] via-[#0F2C50] to-[#2EA3FD] text-white p-5" data-testid="page-preview-hero">
          <p className="text-[10px] uppercase tracking-widest text-[#8FD0FF]">{val(hero, "eyebrow")}</p>
          <h2 className="text-xl font-extrabold font-display mt-1">{val(hero, "title")}</h2>
          {hero.fields.some((f) => f.key === "subtitle") && <p className="text-slate-200 text-sm mt-1.5">{val(hero, "subtitle")}</p>}
        </div>
      )}
      {rest.map((s) => {
        const hidden = s.toggleable && values[s.key]?.enabled === false;
        return (
          <div key={s.key} className={`rounded-lg border p-4 ${hidden ? "opacity-60 border-dashed border-slate-300 bg-slate-50" : "border-slate-200 bg-white"}`} data-testid={`page-preview-section-${s.key}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-[#0B1A2F]">{s.label}</p>
              {s.toggleable && <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${hidden ? "bg-slate-200 text-slate-500" : "bg-emerald-100 text-emerald-700"}`}>{hidden ? "Hidden" : "Shown"}</span>}
            </div>
            {hidden ? (
              <p className="text-xs text-slate-400 italic">This section is switched off and won't appear for staff.</p>
            ) : (
              <div className="space-y-1.5">
                {s.fields.map((f) => {
                  const v = val(s, f.key);
                  if (!v) return null;
                  const strong = f.key.includes("title") || f.key === "heading";
                  const cls = strong ? "text-sm font-semibold text-[#0B1A2F]" : f.type === "url" ? "text-xs text-[#2EA3FD] break-all" : "text-sm text-slate-600";
                  return <p key={f.key} className={cls}>{v}</p>;
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
